/**
 * Geldbeträge in deutscher Schreibweise – geteilt von der Preisliste (Artikel) und den
 * Artikeln am Vertrag, damit beide Ansichten identisch formatieren und parsen.
 *
 * Gerechnet wird grundsätzlich im Backend (BigDecimal, HALF_UP): das Frontend zeigt die
 * gelieferten Beträge nur an, damit es keine Rundungsabweichung zwischen beiden Seiten gibt.
 */

const preisFormat = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
const betragFormat = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Listen-/Einzelpreis mit bis zu 4 Nachkommastellen (die Preisliste führt solche Einträge). */
export const formatPreis = (preis?: number | null): string =>
  preis === null || preis === undefined ? '–' : `${preisFormat.format(preis)} €`;

/** Summen und berechnete Beträge – immer auf Cent gerundet dargestellt. */
export const formatBetrag = (betrag?: number | null): string =>
  betrag === null || betrag === undefined ? '–' : `${betragFormat.format(betrag)} €`;

/** Zahl ohne unnötige Nachkomma-Nullen: 19 → "19", 10 → "10", 7.5 → "7,5", 2.50 → "2,5". */
const kompakt = (zahl: number): string => String(zahl).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '').replace('.', ',');

/** MwSt-Satz als "19 %" bzw. "7,5 %" (ohne unnötige Nullen). */
export const formatMwst = (satz?: number | null): string =>
  satz === null || satz === undefined ? '–' : `${kompakt(satz)} %`;

/** Menge kompakt: 1 → "1", 10 → "10", 2,5 → "2,5". */
export const formatMenge = (menge: number): string => kompakt(menge);

/** Zahl fürs Eingabefeld: deutsche Schreibweise mit Komma, null/undefined = leer. */
export const zahlZuText = (wert?: number | null): string =>
  wert === null || wert === undefined ? '' : String(wert).replace('.', ',');

/**
 * "1.234,56" / "1234,56" / "1234.56" → 1234.56; leer → null; ungültig oder negativ → undefined.
 * Der Aufrufer unterscheidet damit "kein Wert" von "Eingabefehler".
 */
export const textZuZahl = (text: string): number | null | undefined => {
  const roh = text.replace(/[\s€%]/g, '');
  if (roh === '') return null;
  const normalisiert = roh.includes(',') ? roh.replace(/\./g, '').replace(',', '.') : roh;
  const zahl = Number(normalisiert);
  return Number.isFinite(zahl) && zahl >= 0 ? zahl : undefined;
};

/** Deutscher Regelsatz – identisch mit dem DB-Default aus V19 und ArtikelService.STANDARD_MWST. */
export const STANDARD_MWST = 19;
